import React, {useCallback} from "react"
import * as d3 from "d3"
import * as _ from "lodash"
import {
  categories,
  type_counts,
  CATEGORY_NAMES,
  H3_LEVEL,
  mean_sidewalk_score,
  mean_walkability_score,
  mean_social_need_score
} from "./data.js"
import {titleCase} from "./utils.js"

// sidewalk_score
// walkability_score
// social_need_score
const makeComposition = arr => {
  let max = d3.sum(arr)
  return arr.map(d => d / max)
}

export const moduleName = "scoring"

export const sectionOrder = [
  "mapOfBoston",
  "introHex",
  "firstThing",
  "secondThing",
  "thirdThing",
  "allThreeThings"
]

const propHoverInfo = ({hoverFeatures, sectionState}) => {
  let name = {
    sidewalk_score: "sidewalk score",
    walkability_score: "walkability score",
    social_need_score: "social need score"
  }[sectionState.property]

  let meanScore = {
    sidewalk_score: mean_sidewalk_score,
    walkability_score: mean_walkability_score,
    social_need_score: mean_social_need_score
  }[sectionState.property]

  let value = d3.mean(hoverFeatures.map(d => d.properties[sectionState.property]))
  if (value === undefined) {
    return null
  }
  let round = d3.format(".2f")
  let percent = d3.format(".0%")

  let thing = value / meanScore
  console.log("value", value, "meanScore", meanScore, "thing", thing)
  let pc = percent(Math.abs(thing - 1))
  let pcLabel = (
    <span>
      {pc.length === 2 ? <span style={{opacity: 0.001}}>0</span> : ""}
      {pc}
    </span>
  )

  let caption
  switch (sectionState.property) {
    case "sidewalk_score":
      caption = (
        <span>
          <b className={thing >= 1 ? "less" : "more"}>
            {pcLabel} {thing >= 1 ? "worse" : "better"}
          </b>{" "}
          than the average condition.
        </span>
      )
      break
    case "walkability_score":
      caption = (
        <span>
          <b className={thing >= 1 ? "more" : "less"}>
            {pcLabel} {thing >= 1 ? "more" : "less"}
          </b>{" "}
          walkable than average.
        </span>
      )
      break
    case "social_need_score":
      caption = (
        <span>
          <b className={thing >= 1 ? "more" : "less"}>
            {pcLabel} {thing >= 1 ? "more" : "less"}
          </b>{" "}
          social need than average.
        </span>
      )
      break
  }
  return (
    <div className="hover-info">
      <div>
        {caption}
      </div>
    </div>
  )
}

export const sections = {
  mapOfBoston: {
    buttonTitle: "A Map of Boston",
    body: () => (
      <div>
        <h1>A Map of Boston</h1>
        <div className="App-subtitle">Our city and its places</div>
        <p>
          Before looking at data that illustrates some factors driving
          decision-making around improvements and deciding where to fix local issues
          in our city first, let’s orient ourselves by looking at a map of Boston.
        </p>
      </div>
    ),
    defaultState: features => {
      let property = "issues_normalized_by_population"
      let format = d3.format("d")
      return {
        property,
        color: Object.assign(
          d => "transparent",
          d3.scaleSequential(
            d3.extent(features, d => d.properties[property]),
            d3.interpolateRdPu
          )
        ),
        opacity: 0,
        legendFormat: d => format(100 * d)
      }
    }
  },

  introHex: {
    buttonTitle: "Intro to the Hex Grid",
    body: () => (
      <div>
        <h1>Intro to the Data Sets</h1>
        <div className="App-subtitle">From points to hexagons</div>

        <p>
          We’re using three data sets to make decisions about where to fix sidewalks
          first:
        </p>
        <p>
          Sidewalk Scores show us the current condition of local sidewalks,
          Walkability Scores show us how accessible a place is to important civic
          assets like hospitals and libraries, and Social Need Scores tell us about
          how challenging it is for local residents to get around or make a living.
        </p>
        <p>
          This is a lot of data.To evenly see how each factor impacts Boston’s local
          places, we bucket information local to each urban block or area into a
          hexagonal grid. Each hexagon approximates and represents the condition of
          the area it covers.
        </p>
      </div>
    ),
    defaultState: features => {
      let property = "issues_normalized_by_population"
      return {
        property,
        color: Object.assign(
          d => "transparent",
          d3.scaleSequential(
            d3.extent(features, d => d.properties[property]),
            d3.interpolateRdPu
          )
        ),
        stroke: true,
        opacity: 0,
        legendFormat: d3.format("d")
      }
    }
  },

  firstThing: {
    buttonTitle: "Sidewalk Score",
    hoverInfo: propHoverInfo,
    body: ({features, sectionState, setSectionState, hexes, property, color}) => {
      return (
        <div>
          <h1>Scoring Sidewalks</h1>
          <div className="App-subtitle">Where are sidewalks in need of repair?</div>

          <p>
            Here is a hex grid that shows where sidewalks are in need of repair
            across Boston.
          </p>

          <p>
            A lower score indicates that the sidewalk is in good condition while a
            higher score and darker red color indicates that it is in need or
            replacement or repair.
          </p>
        </div>
      )
    },
    defaultState: features => {
      let property = "sidewalk_score"
      return {
        property,
        color: d3.scaleSequential(
          [0, 1],
          d3.interpolateReds
        ),
        opacity: 1,
        legendFormat: d3.format(".0%")
      }
    }
  },

  secondThing: {
    buttonTitle: "Walkability Score",
    hoverInfo: propHoverInfo,
    body: ({features, sectionState, setSectionState, hexes, property, color}) => {
      return (
        <div>
          <h1>Walkability Score</h1>
          <div className="App-subtitle">What sidewalks are most in use?</div>

          <p>
            Here is a hex grid that illuminates areas near shops, hospitals, schools,
            transit stops, and other important assets and amenities that make a place
            accessible and walkable. These locations are therefore places where
            sidewalks are the most trafficked.
          </p>
          <p>
            A higher score indicates that the area is very close to civic assets. The
            more blue the hexagon, the closer it is to more places key to travel,
            health, education, and culture.
          </p>
        </div>
      )
    },
    defaultState: features => {
      let property = "walkability_score"
      return {
        property,
        color: d3.scaleSequential([0, 1], d3.interpolateBlues),
        opacity: 1,
        legendFormat: d3.format(".0%")
      }
    }
  },

  thirdThing: {
    buttonTitle: "Social Need Score",
    hoverInfo: propHoverInfo,
    body: () => (
      <div>
        <h1>Social Need Score</h1>
        <div className="App-subtitle">
          What sidewalks are critical for populations in need?
        </div>

        <p>
          Here is a hex grid that shows where a higher density of people who are in
          social need.
        </p>
        <p>
          A higher social need score indicates that the area has a higher density of
          households in poverty, and citizens that are either very old or very young.
          How would you determine social need?
        </p>

        <p>
          You might notice that there are groupings of hexagons on this map. The data
          on this map comes from the US Census, which provides us with data by census
          tract. Next, we’ll look at a tool that blends data at the scale of the
          census tract with data at the scale of a sidewalk to decide which areas
          should be fixed first.
        </p>
      </div>
    ),
    defaultState: features => {
      let property = "social_need_score"
      return {
        property,
        color: d3.scaleSequential(
          d3.extent(features, d => d.properties[property]),
          d3.interpolateGreens
        ),
        opacity: 1,
        legendFormat: d3.format(".0%")
      }
    }
  },

  allThreeThings: {
    buttonTitle: "The Art of Tradeoffs",
    hoverInfo: ({hoverFeatures, sectionState}) => null,
    body: ({sectionState, setSectionState}) => {
      let {composition} = sectionState
      let round = d3.format(".0%")

      return (
        <div>
          <h1>The Art of Tradeoffs</h1>
          <div className="App-subtitle">
            Combining scores together to make a decision
          </div>
          <p>
            Now we decide how we want to weight these three scores relative to each
            other.
          </p>

          <p>
            Drag the white circle below to make sidewalk condition, walkability, or
            social need more important.
          </p>

          <p>
            The <span style={{color: "#FCFF61"}}>yellow</span> hexagons are the top
            scoring areas based on the combined score you chose. These are the areas
            you have chosen to fix first.
          </p>

          {composition && (
            <div>
              <p className="scores">
                <span className="sidewalk-score">
                  {round(composition[0])} sidewalk
                </span>{" "}
                +{" "}
                <span className="walkability-score">
                  {round(composition[1])} walkability
                </span>{" "}
                +{" "}
                <span className="social-need-score">
                  {round(composition[2])} social need
                </span>
              </p>

              <TriangleSlider
                composition={sectionState.composition}
                onChange={_.throttle(
                  composition => {
                    setSectionState(Object.assign({}, sectionState, {composition}))
                  },
                  250,
                  {leading: true}
                )}
              />
            </div>
          )}
        </div>
      )
    },
    defaultState: features => {
      let property = `tradeoff_score`
      return {
        property,
        color: d3.scaleSequential([0, 1], d3.interpolateViridis),
        opacity: 1,
        legendFormat: d3.format(".0%"),
        composition: [0.1, 0.1, 0.8]
      }
    }
  }
}

const triArea = (a, b, c) => {
  let [xa, ya] = a,
    [xb, yb] = b,
    [xc, yc] = c
  return 0.5 * (xa * yb + xb * yc + xc * ya - xa * yc - xc * yb - xb * ya)
}

let TriangleSlider = ({composition, onChange}) => {
  let areaGen = d3.area()
  let R = 40
  let {PI, sin, cos, sqrt, abs, pow, max} = Math
  let pi = PI,
    twopi = 2 * PI,
    halfpi = PI / 2
  let o = -halfpi
  let a = [cos(o), sin(o)]
  let b = [cos(o + twopi / 3), sin(o + twopi / 3)]
  let c = [cos(o + (2 * twopi) / 3), sin(o + (2 * twopi) / 3)]
  let pathData = areaGen([a, b, c, a])

  let event = p => {
    console.log(p)
    let total = triArea(a, b, c)
    let areas = makeComposition(
      [triArea(b, c, p), triArea(c, a, p), triArea(a, b, p)].map(
        area => max(0, area) / total
      )
    )
    onChange(areas)
    return areas
  }

  const triRef = useCallback(node => {
    if (node !== null) {
      let _node = d3.select(node)
      let dragged = () => {
        // [d3.event.x / R, d3.event.y / R]
        let areas = event(d3.mouse(node, node.parentElement))
        // todo: set the circle position immediately
        let x = R * d3.sum([a[0] * areas[0], b[0] * areas[1], c[0] * areas[2]])
        let y = R * d3.sum([a[1] * areas[0], b[1] * areas[1], c[1] * areas[2]])
        console.log(circleRef)
        circleRefCurrent &&
          d3
            .select(circleRefCurrent)
            .attr("cx", x)
            .attr("cy", y)
      }
      _node.call(
        d3
          .drag()
          .on("drag", dragged)
          .on("start", dragged)
      )
    }
  }, [])

  let circleRefCurrent = null
  const circleRef = useCallback(node => {
    if (node !== null) {
      circleRefCurrent = node
    }
  }, [])

  let areas = composition
  let x = R * d3.sum([a[0] * areas[0], b[0] * areas[1], c[0] * areas[2]])
  let y = R * d3.sum([a[1] * areas[0], b[1] * areas[1], c[1] * areas[2]])

  return (
    <svg
      className="TriangleSlider"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width="400"
      height="300"
      viewBox="-50 -50 100 75">
      <defs>
        <linearGradient
          id="a-grad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
          gradientTransform="rotate(90)">
          <stop offset="0%" stopColor="rgb(255,0,0,1)" />
          <stop offset="100%" stopColor="rgb(255,255,255,0)" />
        </linearGradient>
        <linearGradient
          id="b-grad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
          gradientTransform="rotate(90)">
          <stop offset="0%" stopColor="rgb(0,0,255,1)" />
          <stop offset="100%" stopColor="rgb(255,255,255,0)" />
        </linearGradient>
        <linearGradient
          id="c-grad"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
          gradientTransform="rotate(90)">
          <stop offset="0%" stopColor="rgb(0,255, 0,1)" />
          <stop offset="100%" stopColor="rgb(255,255,255,0)" />
        </linearGradient>
      </defs>

      <g transform={`scale(${R}, ${R})`}>
        <path
          d={pathData}
          ref={triRef}
          pointerEvents="all"
          stroke="transparent"
          fill={`url(${""}#a-grad)`}
          transform="rotate(0)"
        />
        <path
          d={pathData}
          pointerEvents="none"
          stroke="transparent"
          fill={`url(${""}#b-grad)`}
          transform="rotate(120)"
        />
        <path
          d={pathData}
          pointerEvents="none"
          stroke="transparent"
          fill={`url(${""}#c-grad)`}
          transform="rotate(240)"
        />
      </g>
      <circle
        ref={circleRef}
        cx={x}
        cy={y}
        fill={"#fff"}
        stroke={"#666"}
        r={4.5}
        strokeWidth={0.5}
        pointerEvents="none"
      />
      {[a, b, c].map(([x, y], i) => (
        <circle
          pointerEvents="none"
          key={i}
          r="1"
          cx={R * x}
          cy={R * y}
          fill="#fff"
        />
      ))}
    </svg>
  )
}

export const appIntro = (
  <>
    <h1>Decide Where Change Happens</h1>
    <div className="App-subtitle">
      Can data be used for more sensitive decision-making?
    </div>
    <div className="App-intro-ps">
      <p>
        Data about our cities, neighborhoods, and streets is growing due to the
        increase of new technologies that collect information.
      </p>

      <p>
        When cities make decisions about where and how to make changes to the shared
        urban places in our public realm, a variety of factors come into play.
      </p>

      <p>
        If you had to make a decision about where to improve or replace local
        sidewalks, what would you do?
      </p>
    </div>
  </>
)
